@use '../../scss/global' as *;
@use '../../scss/components' as *;
@use 'themes';

// ================================================================ //
// -- Local Variables

// ================================================================ //
// -- Mixins

@mixin alert-dismiss-button(
  $color-var // CSS custom property for the "dismiss" Button color
) {
  .alert-dismiss {
    @include close-button(
      $color-default: $color-var,
      $color-hover: darken-css-var($color-var, 10%),
      $background-hover: transparent,
      $background-active: transparent
    );
  }
}

// ================================================================ //
// -- Component Styles

:host {
  display: block;

  .fluid-alert {
    display: flex;
    column-gap: var(--alert-body-padding);
    position: relative;
    line-height: var(--alert-line-height);
    margin-bottom: 1em;
    border-style: solid;
    border-width: var(--G-border-width);
    border-left-width: var(--accent-border-width);
    border-radius: var(--G-border-radius);
    padding-top: var(--alert-body-padding);
    padding-bottom: var(--alert-body-padding);
    padding-left: var(--alert-body-padding);
    padding-right: calc(var(--alert-body-padding) * 1.5);
    will-change: visibility, opacity;

    // == Success

    &.alert-success {
      background-color: var(--G-color-success-bg);
      border-color: var(--G-color-success);

      @include alert-dismiss-button(var(--G-color-success-fill));
    }

    // == Info

    &.alert-info {
      background-color: var(--G-color-info-bg);
      border-color: var(--G-color-info);

      @include alert-dismiss-button(var(--G-color-info-fill));
    }

    // == Warning

    &.alert-warning {
      background-color: var(--G-color-warning-bg);
      border-color: var(--G-color-warning);
    }

    // == Error

    &.alert-error {
      background-color: var(--G-color-error-bg);
      border-color: var(--G-color-error);
    }

    // == Fade In / Fade Out Animations

    &.fade-in {
      @include fade-in(short);
    }

    &.fade-out {
      @include fade-out(medium);
    }

    // == Dismissible

    &.dismissible {
      .alert-body {
        margin-right: 1em;
      }
    }

    // == Remove Bottom Margin

    &.no-margin {
      margin-bottom: 0;
    }

    // == Snackbar Alert

    &.snackbar-alert {
      width: max-content;
      min-width: 20em;
      max-width: min(50em, 100% - 2em);
      position: fixed;
      box-shadow: var(--snackbar-box-shadow);

      @include screen-md {
        max-width: min(50em, 100% - 4em);
      }

      &.top-left,
      &.top-center,
      &.top-right {
        top: 1.5em;
      }

      &.bottom-left,
      &.bottom-center,
      &.bottom-right {
        bottom: 5vh;
      }

      &.top-left,
      &.bottom-left {
        left: 1em;

        @include screen-md {
          left: 1.5em;
        }
      }

      &.top-center,
      &.bottom-center {
        left: 50%;
        transform: translateX(-50%);
      }

      &.top-right,
      &.bottom-right {
        right: 1em;

        @include screen-md {
          right: 1.5em;
        }
      }
    }

    // == Hidden

    &[hidden] {
      display: none !important;
    }

    // ==== Icon Container

    .alert-icon {
      flex: 0 0 auto;
      height: 1lh;
      display: flex;
      justify-content: center;
      align-items: center;
      color: var(--alert-icon-color); // Sets FLUID Icon "fill" color
      fill: currentColor; // Sets external icon "fill" color
    }

    // ==== Alert Body

    .alert-body {
      flex: 1 1 auto;
    }

    // ==== Alert Title

    .alert-title {
      color: var(--alert-title-color, var(--G-color-text));
      padding-bottom: 0.125em;

      .title-text {
        display: block;
        font-weight: var(--alert-title-font-weight);
        font-size: 1.0625em;
      }
    }

    // ==== Message Container

    .alert-message {
      display: flex;
      color: var(--alert-message-color, var(--G-color-text));
    }

    // ==== Dismiss Button

    .alert-dismiss {
      position: absolute;
      top: 0;
      right: 0;
    }
  }
}

// ==== Inline Alert

:host(.fluid-alert--inline) {
  display: inline-block;
  vertical-align: middle;
}

// ==== Hidden Alert

:host(.fluid-alert--hidden) {
  display: none;
}
